/**
 * Les lieux où un employé pointe : le bureau de la société, le dépôt (le
 * magasinier), le chantier — comme toujours — et la maison (télétravail).
 *
 * <p>Les adresses et les positions du bureau et du dépôt se règlent une fois
 * (Réglages → Société → Adresses de travail) et vivent dans les paramètres de
 * la société, sous {@link CLE_LIEUX_TRAVAIL}. La fiche de l'employé dit où il
 * travaille d'habitude, où il peut aussi pointer et s'il fait du télétravail.
 *
 * <p>Pointer loin du lieu choisi n'empêche rien : l'heure est notée, avec
 * « hors du bureau » ou « hors du dépôt ». Le télétravail un jour non prévu
 * est noté aussi, « à accepter » par le patron.
 */
import { distLisible, distM } from './geo';
import { tr, trf } from './i18n';

/** Clé des paramètres de la société : adresses, positions et distance acceptée. */
export const CLE_LIEUX_TRAVAIL = 'lieux.travail';

/** Les pointages de télétravail non prévu que le patron a acceptés (identifiants). */
export const CLE_TELETRAVAIL_ACCEPTE = 'lieux.teletravailAcceptes';

/** Les distances proposées, en mètres. */
export const RAYONS = [100, 200, 500];
export const RAYON_DEFAUT = 200;

export const BUREAU = 'BUREAU';
export const DEPOT = 'DEPOT';
export const CHANTIER = 'CHANTIER';
export const TELETRAVAIL = 'TELETRAVAIL';

/** Les lieux qu'on pointe sans chantier (badges de la fiche de l'employé). */
export const LIEUX_SANS_CHANTIER = [BUREAU, DEPOT, TELETRAVAIL];

/**
 * Le nom d'un lieu, avec son pictogramme.
 *
 * @param {string} lieu BUREAU, DEPOT, CHANTIER ou TELETRAVAIL
 * @returns {string} le libellé traduit
 */
export function libelleLieu(lieu) {
  if (lieu === BUREAU) return tr('🏢 Bureau');
  if (lieu === DEPOT) return tr('📦 Dépôt');
  if (lieu === TELETRAVAIL) return tr('🏠 Télétravail');
  return tr('🏗 Chantiers');
}

const position = (p) =>
  p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;

/**
 * Les adresses de travail réglées pour la société.
 *
 * @param {object|null|undefined} brut valeur lue par `reglageJson(CLE_LIEUX_TRAVAIL, {})`
 * @returns {{bureau: {lat:number,lng:number}|null, depot: {lat:number,lng:number}|null,
 *            depotAdresse: string, rayon: number}} les réglages, défauts compris
 */
export function lieuxDeLaSociete(brut) {
  const r = brut && typeof brut === 'object' ? brut : {};
  const rayon = RAYONS.includes(Number(r.rayon)) ? Number(r.rayon) : RAYON_DEFAUT;
  return {
    bureau: position(r.bureau),
    depot: position(r.depot),
    depotAdresse: typeof r.depotAdresse === 'string' ? r.depotAdresse : '',
    rayon,
  };
}

/** « CHANTIER,DEPOT » → ['CHANTIER', 'DEPOT'] ; un tableau passe tel quel. */
export function listeCsv(valeur) {
  if (Array.isArray(valeur)) return valeur.filter(Boolean);
  return String(valeur ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Les jours de télétravail prévus, numéros ISO (1 = lundi … 7 = dimanche). */
export function joursCsv(valeur) {
  return listeCsv(valeur)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7);
}

/**
 * Le lieu habituel de l'employé : le chantier quand rien n'est réglé, comme
 * avant que les lieux existent.
 */
export function lieuHabituel(emp) {
  const l = emp?.lieuHabituel;
  return l === BUREAU || l === DEPOT ? l : CHANTIER;
}

/** L'employé a-t-il un réglage de lieu (autre que « chantier comme toujours ») ? */
export function aDesLieux(emp) {
  return (
    lieuHabituel(emp) !== CHANTIER ||
    listeCsv(emp?.lieuxAutres).some((l) => l !== CHANTIER) ||
    teletravailPermis(emp)
  );
}

/** Le télétravail lui est-il ouvert (tous les jours ou certains jours) ? */
export function teletravailPermis(emp) {
  return emp?.teletravail === 'TOUS' || emp?.teletravail === 'JOURS';
}

/** Le jour ISO (1 = lundi) d'une date « AAAA-MM-JJ ». */
export function jourIso(dateIso) {
  const [a, m, j] = String(dateIso).split('-').map(Number);
  const d = new Date(a, (m || 1) - 1, j || 1);
  return ((d.getDay() + 6) % 7) + 1;
}

/**
 * Le télétravail est-il prévu ce jour-là ? Tous les jours : oui ; certains
 * jours : seulement ceux cochés ; sinon non.
 */
export function teletravailPrevu(emp, dateIso) {
  if (emp?.teletravail === 'TOUS') return true;
  if (emp?.teletravail !== 'JOURS') return false;
  return joursCsv(emp.teletravailJours).includes(jourIso(dateIso));
}

/**
 * Les lieux qu'il peut choisir en pointant : son lieu habituel d'abord, puis
 * les autres, puis le télétravail s'il lui est ouvert.
 */
export function lieuxPermis(emp) {
  const liste = [lieuHabituel(emp)];
  for (const l of listeCsv(emp?.lieuxAutres)) {
    if ([BUREAU, DEPOT, CHANTIER].includes(l) && !liste.includes(l)) liste.push(l);
  }
  if (teletravailPermis(emp)) liste.push(TELETRAVAIL);
  return liste;
}

/**
 * Contrôle la position au bureau ou au dépôt.
 *
 * @param {string} lieu BUREAU ou DEPOT (le télétravail n'est pas contrôlé)
 * @param {number|null} lat latitude relevée, nulle sans position
 * @param {number|null} lng longitude relevée
 * @param {ReturnType<typeof lieuxDeLaSociete>} societe les adresses réglées
 * @returns {{surPlace: boolean|null, distance: number|null, sansPosition: boolean}}
 *   `surPlace` nul quand on ne peut rien vérifier ; `sansPosition` vrai quand
 *   la position du lieu n'est pas encore enregistrée
 */
export function controleLieu(lieu, lat, lng, societe) {
  const cible = lieu === BUREAU ? societe.bureau : lieu === DEPOT ? societe.depot : null;
  if (lieu !== BUREAU && lieu !== DEPOT)
    return { surPlace: null, distance: null, sansPosition: false };
  if (!cible) return { surPlace: null, distance: null, sansPosition: true };
  const distance = distM(lat, lng, cible.lat, cible.lng);
  if (distance == null) return { surPlace: null, distance: null, sansPosition: false };
  return { surPlace: distance <= societe.rayon, distance, sansPosition: false };
}

/** Arrondit une coordonnée à 7 décimales, la précision gardée par le serveur. */
export function coordonnee(valeur) {
  return typeof valeur === 'number' && Number.isFinite(valeur)
    ? Math.round(valeur * 1e7) / 1e7
    : null;
}

/**
 * Le constat d'un pointage sans chantier, pour le relevé : sur place, hors du
 * lieu, ou télétravail à accepter.
 *
 * @param {object} badge pointage de la fiche (lieu, onSiteEntree…)
 * @param {object} emp l'employé, pour ses jours de télétravail
 * @param {Array<string|number>} [acceptes] pointages de télétravail acceptés par le patron
 * @returns {'ok'|'hors'|'accepter'|'accepte'|null} le constat, nul s'il n'y a rien à dire
 */
export function constatDuBadge(badge, emp, acceptes = []) {
  if (!badge) return null;
  if (badge.lieu === TELETRAVAIL) {
    if (teletravailPrevu(emp, badge.date)) return null;
    return badge.id != null && (acceptes || []).map(String).includes(String(badge.id))
      ? 'accepte'
      : 'accepter';
  }
  if (badge.lieu !== BUREAU && badge.lieu !== DEPOT) return null;
  if (badge.onSiteEntree === false || badge.onSiteSortie === false) return 'hors';
  if (badge.onSiteEntree === true) return 'ok';
  return null;
}

/** La marque d'un constat, glissée après le lieu : « ✓ », « ⚠ », « à accepter » ou « ✓ accepté ». */
export function marqueDuConstat(constat) {
  if (constat === 'ok') return tr('✓ sur place');
  if (constat === 'hors') return tr('⚠ hors du lieu');
  if (constat === 'accepter') return tr('à accepter');
  if (constat === 'accepte') return tr('✓ accepté');
  return '';
}

/**
 * Les lieux du relevé d'heures d'un employé : ses chantiers, puis les heures
 * pointées sur sa fiche, rangées par lieu — atelier, bureau, dépôt,
 * télétravail, et ce qui reste « sans chantier ». Au magasin, le magasin seul.
 *
 * @param {object} emp l'employé, avec ses badges
 * @param {object[]} chantiers les chantiers, avec leurs pointages
 * @param {object} [options] adresses et univers
 * @param {object} [options.soc] la société (adresse du bureau, du magasin)
 * @param {string} [options.depotAdresse] adresse du dépôt
 * @param {boolean} [options.magasin] l'univers du magasin
 * @returns {object[]} les lieux, chacun avec son nom, son adresse et ses pointages
 */
export function lieuxDuReleve(emp, chantiers, { soc, depotAdresse = '', magasin = false } = {}) {
  const surSaFiche = (emp?.badges || [])
    .filter((b) => b.chId == null)
    .map((b) => ({ ...b, empId: emp.id }));
  if (magasin)
    return [
      {
        nom: tr('Magasin'),
        adr: soc?.adr || '',
        npa: soc?.npa || '',
        pointages: surSaFiche,
        rapports: [],
      },
    ];
  const de = (lieu) => surSaFiche.filter((b) => b.lieu === lieu);
  const place = (nom, pointages, adr = '', npa = '') =>
    pointages.length ? [{ nom, adr, npa, pointages, rapports: [], horsChantier: true }] : [];
  // Le travail général de l'atelier a son propre lieu : il ne se confond pas
  // avec une journée oubliée sans chantier ; le bureau, le dépôt et la maison
  // non plus.
  const connus = ['ATELIER', ...LIEUX_SANS_CHANTIER];
  return [
    ...(chantiers || []),
    ...place(tr('🏭 Atelier'), de('ATELIER')),
    ...place(libelleLieu(BUREAU), de(BUREAU), soc?.adr || '', soc?.npa || ''),
    ...place(libelleLieu(DEPOT), de(DEPOT), depotAdresse),
    ...place(libelleLieu(TELETRAVAIL), de(TELETRAVAIL)),
    ...place(
      tr('Sans chantier'),
      surSaFiche.filter((b) => !connus.includes(b.lieu)),
    ),
  ];
}

/**
 * Le message qui suit le pointage au bureau, au dépôt ou en télétravail.
 *
 * @param {string} lieu BUREAU, DEPOT ou TELETRAVAIL
 * @param {{surPlace: boolean|null, distance: number|null, sansPosition: boolean}|null} controle
 *   le contrôle de position (nul en télétravail)
 * @param {object} emp l'employé (télétravail permis, jours prévus)
 * @param {string} jour date du pointage
 * @returns {{ton: string, texte: string}|null} le constat à afficher
 */
export function messageDuLieu(lieu, controle, emp, jour) {
  if (lieu === TELETRAVAIL) {
    return teletravailPrevu(emp, jour)
      ? null
      : {
          ton: 'alerte',
          texte: tr(
            '🏠 Télétravail non prévu aujourd’hui : ta journée est notée et le patron doit l’accepter.',
          ),
        };
  }
  if (lieu !== BUREAU && lieu !== DEPOT) return null;
  if (!controle) return null;
  if (controle.sansPosition) {
    return {
      ton: 'info',
      texte: tr('Position du lieu pas encore enregistrée : journée notée sans vérification.'),
    };
  }
  if (controle.surPlace == null) {
    return {
      ton: 'info',
      texte: tr('Position introuvable : journée notée sans vérification.'),
    };
  }
  const distance = distLisible(controle.distance);
  if (controle.surPlace) {
    return {
      ton: 'ok',
      texte:
        lieu === BUREAU
          ? trf('✓ Tu es au bureau ({0})', distance)
          : trf('✓ Tu es au dépôt ({0})', distance),
    };
  }
  const hors =
    lieu === BUREAU
      ? trf('⚠ Tu es à {0} du bureau. Ta journée est notée « hors du bureau ».', distance)
      : trf('⚠ Tu es à {0} du dépôt. Ta journée est notée « hors du dépôt ».', distance);
  return {
    ton: 'alerte',
    texte: teletravailPermis(emp)
      ? hors + ' ' + tr('Choisis 🏠 Télétravail si tu travailles de chez toi.')
      : hors,
  };
}

/** Au-delà, on se passe de la position : l'heure ne doit jamais attendre. */
export const DELAI_POSITION_MS = 10000;

/**
 * Demande la position une fois, sans jamais bloquer : nulle si le téléphone
 * refuse, ne sait pas, ou tarde.
 *
 * @returns {Promise<{lat: number|null, lng: number|null}>} la position
 */
export function positionDuTelephone() {
  return new Promise((resoudre) => {
    let fini = false;
    const une = (lat, lng) => {
      if (fini) return;
      fini = true;
      resoudre({ lat, lng });
    };
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      une(null, null);
      return;
    }
    try {
      navigator.geolocation.getCurrentPosition(
        (po) => une(po.coords.latitude, po.coords.longitude),
        () => une(null, null),
        { timeout: DELAI_POSITION_MS, maximumAge: 60000 },
      );
    } catch {
      une(null, null);
    }
    setTimeout(() => une(null, null), DELAI_POSITION_MS);
  });
}
